import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { usePermission } from "../../hooks/usePermissions";
import styles from "./AccessRights.module.css";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import AddAccessRights from "./AddAccessRights";

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const SaveIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
);

const AccessRights = () => {
    const navigate = useNavigate();
    const [accessRights, setAccessRights] = useState([]);
    const [editingRowId, setEditingRowId] = useState(null);
    const [localPermissions, setLocalPermissions] = useState({});
    const [roles, setRoles] = useState([]);
    const [accessTypesMap, setAccessTypesMap] = useState({});
    const [selectedRole, setSelectedRole] = useState("");
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const itemsPerPage = 10;

    const { hasPermission } = usePermission();

    const yesNoToBool = useCallback((value) => value === "Yes", []);
    const boolToYesNo = useCallback((bool) => (bool ? "Yes" : "No"), []);

    const fetchAccessRights = useCallback(async (roleId = null) => {
        setLoading(true);
        try {
            const response = await api.get("/access-rights", {
                params: roleId ? { role_id: roleId } : {}
            });

            const data = response.data || [];
            setAccessRights(data);

            const initialLocal = {};
            data.forEach((row) => {
                initialLocal[row.id] = {
                    create_page: yesNoToBool(row.create_page),
                    view_page: yesNoToBool(row.view_page),
                    modify_page: yesNoToBool(row.modify_page),
                    delete_page: yesNoToBool(row.delete_page),
                };
            });
            setLocalPermissions(initialLocal);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load access rights");
        } finally {
            setLoading(false);
        }
    }, [yesNoToBool]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [rolesRes, typesRes] = await Promise.all([
                    api.get("/access-roles"),
                    api.get("/access-types")
                ]);
                setRoles(rolesRes.data || []);

                // Build a map: access type name → access_portal
                const typesData = typesRes.data || [];
                const portalMap = {};
                typesData.forEach((t) => {
                    portalMap[t.access_types] = t.access_portal || "";
                });
                setAccessTypesMap(portalMap);

                await fetchAccessRights();
            } catch (error) {
                console.error("Initial load error:", error);
            }
        };
        fetchData();
    }, [fetchAccessRights]);

    const handleCheckboxChange = (rowId, permKey) => {
        setLocalPermissions(prev => ({
            ...prev,
            [rowId]: {
                ...prev[rowId],
                [permKey]: !prev[rowId][permKey]
            }
        }));
    };

    const handleSaveClick = async (row) => {
        const rowKey = `${row.id}`;
        const perms = localPermissions[rowKey] || {};
        try {
            const permissionsToSend = {
                create_page: boolToYesNo(perms.create_page),
                view_page: boolToYesNo(perms.view_page),
                modify_page: boolToYesNo(perms.modify_page),
                delete_page: boolToYesNo(perms.delete_page),
            };

            setAccessRights(prev =>
                prev.map(r => (r.id === row.id ? { ...r, ...permissionsToSend } : r))
            );

            await api.put(`/access-rights/update`, {
                id: row.id,
                permissions: permissionsToSend
            });

            toast.success("Permissions updated successfully");
            setEditingRowId(null);
        } catch (error) {
            console.error("Save error:", error);
            toast.error("Failed to update permissions");
        }
    };

    return (
        <div className={styles.container}>
            <ToastContainer position="bottom-right" autoClose={2000} />

            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Access Rights</h1>
                    <p className={styles.pageSubtitle}>Configure granular permissions for each module and role</p>
                </div>

                {hasPermission("CREATE_ACCESSCONTROL") && (
                <button
                    className={styles.addBtn}
                    onClick={() => setIsModalOpen(true)}
                >
                    + Add Right
                </button>
                )}
            </div>

            <div className={styles.filtersBar}>
                <div className={styles.filterGroup}>
                    <select
                        className={styles.select}
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                    >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                                {role.role_type}
                            </option>
                        ))}
                    </select>
                    <button
                        className={styles.searchBtn}
                        onClick={() => {
                            if (!selectedRole) {
                                toast.warn("Please select a role");
                                return;
                            }
                            setCurrentPage(1);
                            fetchAccessRights(selectedRole);
                        }}
                    >
                        Search
                    </button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                {(() => {
                    const totalPages = Math.max(1, Math.ceil(accessRights.length / itemsPerPage));
                    const paginatedRights = accessRights.slice(
                        (currentPage - 1) * itemsPerPage,
                        currentPage * itemsPerPage
                    );

                    return (
                        <>
                            <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Module</th>
                            <th>Role</th>
                            <th>Platform</th>
                            <th className={styles.w100px}>Create</th>
                            <th className={styles.w100px}>View</th>
                            <th className={styles.w100px}>Modify</th>
                            <th className={styles.w100px}>Delete</th>
                            <th className={styles.w100px}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && accessRights.length === 0 ? (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>Loading access rights...</td>
                            </tr>
                        ) : paginatedRights.length > 0 ? (
                            paginatedRights.map((row) => {
                                const rowKey = `${row.id}`;
                                const isEditing = editingRowId === rowKey;
                                const perms = localPermissions[rowKey] || {};
                                const portal = row.access_portal || accessTypesMap[row.access_types] || "";

                                return (
                                    <tr key={row.id}>
                                        <td className={styles.fontMedium}>{row.access_types}</td>
                                        <td>{row.access_roles}</td>
                                        <td>{portal || "—"}</td>
                                        <td>
                                            <div className={styles.permCell}>
                                                {isEditing ? (
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={!!perms.create_page}
                                                        onChange={() => handleCheckboxChange(rowKey, "create_page")}
                                                    />
                                                ) : (
                                                    <span className={styles.statusIcon}>{row.create_page === "Yes" ? "✅" : "❌"}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.permCell}>
                                                {isEditing ? (
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={!!perms.view_page}
                                                        onChange={() => handleCheckboxChange(rowKey, "view_page")}
                                                    />
                                                ) : (
                                                    <span className={styles.statusIcon}>{row.view_page === "Yes" ? "✅" : "❌"}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.permCell}>
                                                {isEditing ? (
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={!!perms.modify_page}
                                                        onChange={() => handleCheckboxChange(rowKey, "modify_page")}
                                                    />
                                                ) : (
                                                    <span className={styles.statusIcon}>{row.modify_page === "Yes" ? "✅" : "❌"}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.permCell}>
                                                {isEditing ? (
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={!!perms.delete_page}
                                                        onChange={() => handleCheckboxChange(rowKey, "delete_page")}
                                                    />
                                                ) : (
                                                    <span className={styles.statusIcon}>{row.delete_page === "Yes" ? "✅" : "❌"}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className={styles.actionsCell}>
                                                {hasPermission("MODIFY_ACCESSCONTROL") && (
                                                    <button
                                                        className={`${styles.iconBtn} ${isEditing ? styles.save : styles.edit}`}
                                                        onClick={() => (isEditing ? handleSaveClick(row) : setEditingRowId(rowKey))}
                                                        title={isEditing ? "Save" : "Edit"}
                                                    >
                                                        {isEditing ? <SaveIcon /> : <EditIcon />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={8} className={styles.emptyState}>No access rights found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {!loading && accessRights.length > 0 && (
                    <div className={styles.tableFooter}>
                        <span>
                            Showing {paginatedRights.length} of {accessRights.length} rights
                        </span>

                        {totalPages > 1 && (
                            <div className={styles.pagination}>
                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((prev) => prev - 1)}
                                >
                                    {"<"}
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                type="button"
                                                key={page}
                                                className={`${styles.pageBtn} ${currentPage === page ? styles.active : ""}`}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    }

                                    if (page === currentPage - 2 || page === currentPage + 2) {
                                        return (
                                            <span key={page} className={styles.paginationEllipsis}>
                                                ...
                                            </span>
                                        );
                                    }

                                    return null;
                                })}

                                <button
                                    type="button"
                                    className={styles.pageBtn}
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((prev) => prev + 1)}
                                >
                                    {">"}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                </>
                );
            })()}
            </div>
            
            {isModalOpen && (
                <FullscreenModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Create Access Right"
                    maxWidth="600px"
                    disableBackdropClick={true}
                >
                    <AddAccessRights
                        rightId={null}
                        onSubmitted={() => {
                            setIsModalOpen(false);
                            fetchAccessRights(selectedRole);
                        }}
                        onCancel={() => setIsModalOpen(false)}
                    />
                </FullscreenModal>
            )}
        </div>
    );
};

export default AccessRights;